import { useEffect, useRef, useState, useCallback } from 'react';

// Hook: fade/slide elements in when they enter the viewport
export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { threshold = 0.15, rootMargin = '0px 0px -40px 0px' } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Respect prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, isVisible];
}

// Hook: parallax background shift on scroll (hero only)
export function useParallax(speed = 0.35) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const rect = ref.current.getBoundingClientRect();
    // Only calculate when element is near viewport
    if (rect.bottom > 0 && rect.top < window.innerHeight) {
      setOffset(window.scrollY * speed);
    }
  }, [speed]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return [ref, offset];
}

// Wrapper component for scroll-reveal sections
export function RevealSection({ children, className = '', direction = 'up', delay = 0, ...props }) {
  const [ref, isVisible] = useScrollReveal();

  const directionStyles = {
    up: 'translate-y-8',
    down: '-translate-y-8',
    left: 'translate-x-8',
    right: '-translate-x-8',
    none: '',
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 ${directionStyles[direction]}`
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}
    >
      {children}
    </div>
  );
}

// Staggered children reveal - grid safe
export function StaggerReveal({ children, className = '', staggerMs = 100, direction = 'up' }) {
  const [ref, isVisible] = useScrollReveal();

  const dirMap = { up: 'translateY(24px)', down: 'translateY(-24px)', left: 'translateX(24px)', right: 'translateX(-24px)', none: 'none' };

  const childArray = [];
  const flatten = (items) => {
    if (Array.isArray(items)) { items.forEach(flatten); }
    else if (items != null && items !== false) { childArray.push(items); }
  };
  flatten(children);

  return (
    <div ref={ref} className={className}>
      {childArray.map((child, i) => (
        <div
          key={child?.key || i}
          style={{
            transition: `opacity 0.5s ease-out ${i * staggerMs}ms, transform 0.5s ease-out ${i * staggerMs}ms`,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'none' : dirMap[direction],
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
